package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceResponse
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.CalendarScrollStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.HeadTableRowVariant
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.InvoiceTableRowStyle
import ai.jessie.web.theme.InvoiceTableStyle
import ai.jessie.web.theme.NumStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.TableIconStyle
import ai.jessie.web.theme.TableNumHeadStyle
import ai.jessie.web.theme.TableNumStyle
import ai.jessie.web.theme.TableRowKind
import ai.jessie.web.theme.TableRowStyle
import ai.jessie.web.theme.TableStrongStyle
import ai.jessie.web.theme.TableStyle
import ai.jessie.web.theme.icon
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

// Step 2: only the invoices the server returned for this organisation, in its order.
@Composable
internal fun invoiceList(
    organisationId: String,
    state: InvoiceUiState,
    actions: InvoiceActions,
    focusReturn: FocusReturn,
) {
    invoiceFocus(state, focusReturn)
    val invoices = state.invoices
    if (invoices != null) {
        if (invoices.isEmpty()) P(attrs = HintStyle.toAttrs()) { Text("Noch keine Rechnungen.") }
        else invoiceTable(organisationId, invoices)
    }
    Div(attrs = RowStyle.toAttrs()) {
        themedButton("Rechnungsliste erneut laden", actions.refresh, disabled = state.busy)
    }
}

@Composable
private fun invoiceTable(organisationId: String, invoices: List<InvoiceResponse>) {
    Div(attrs = CardStyle.toAttrs()) {
        Div(attrs = CalendarScrollStyle.toAttrs()) {
            Div(
                attrs =
                    TableStyle.toModifier().then(InvoiceTableStyle.toModifier()).toAttrs {
                        attr("role", "table")
                        attr("aria-label", INVOICES_TITLE)
                    }
            ) {
                invoiceRow(HeadTableRowVariant) {
                    listOf("Nummer", "Rechnungsdatum", "Empfänger", "Status").forEach {
                        Span(attrs = { attr("role", "columnheader") }) { Text(it) }
                    }
                    Span(attrs = TableNumHeadStyle.toAttrs { attr("role", "columnheader") }) {
                        Text("Total")
                    }
                    Span(attrs = { attr("role", "columnheader") })
                }
                invoices.forEach { invoiceEntryRow(organisationId, it) }
            }
        }
    }
}

// Amounts are the server's gross total with its currency; a Draft has no number (UC-015 B6).
@Composable
private fun invoiceEntryRow(organisationId: String, invoice: InvoiceResponse) {
    val href = "/organisations/$organisationId/invoices/${invoice.id}"
    invoiceRow(null) {
        Span(attrs = NumStyle.toAttrs { attr("role", "cell") }) { Text(invoice.number.orEmpty()) }
        Span(attrs = { attr("role", "cell") }) { Text(invoice.issueDate) }
        Span(attrs = { attr("role", "cell") }) {
            A(href = href, attrs = TableStrongStyle.toAttrs()) { Text(invoice.recipientName) }
        }
        Span(attrs = { attr("role", "cell") }) { statusBadge(invoice.status) }
        Span(attrs = TableNumStyle.toAttrs { attr("role", "cell") }) {
            Text(money(invoice.currency, invoice.totals.gross))
        }
        Span(attrs = TableIconStyle.toAttrs { attr("role", "cell") }) {
            A(
                href = href,
                attrs = {
                    attr("aria-label", "Rechnung ${invoice.number ?: invoice.recipientName} öffnen")
                },
            ) {
                icon("chevron-right", size = 12)
            }
        }
    }
}

@Composable
private fun invoiceRow(variant: CssStyleVariant<TableRowKind>?, cells: @Composable () -> Unit) {
    Div(
        attrs =
            TableRowStyle.toModifier(variant).then(InvoiceTableRowStyle.toModifier()).toAttrs {
                attr("role", "row")
            }
    ) {
        cells()
    }
}
